import EmailRounded from '@mui/icons-material/EmailRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { errorMessage, useNotify } from '../../lib/notify';
import { fetchTenant, tenantKeys } from '../tenants/api';
import { Callout } from '../tenants/parts';
import { useSendTenantMessage, type Channel, type PlatformMessage } from './api';
import { RichTextEditor } from '../../components/RichTextEditor';
import { htmlText } from '../../lib/richText';
import {
  fillEmailTemplate,
  fillTemplate,
  useTemplates,
  type TemplateKind,
  type TemplateVars,
} from './templates';

export const WHATSAPP_GREEN = '#1DA851';

/**
 * Sends a shop a WhatsApp message or an email from PackBytes' own accounts (Settings), starting from
 * the Settings template filled for this shop (email bodies are rich text). The shop's owners and its
 * contact details are offered as recipients. With codeId (a new activation code), `extra` carries the
 * code's lines and the code is marked delivered once the message went out.
 */
export function SendMessageDialog({
  tenant,
  channel: initial,
  template = 'tenant',
  extra,
  prefer,
  codeId,
  onSent,
  onClose,
}: {
  tenant: { id: string; name: string };
  channel: Channel;
  /** Which of the Settings templates the message starts with. */
  template?: TemplateKind;
  /** Added after the template (an activation code's details): text for WhatsApp, HTML for email. */
  extra?: { text: string; html: string };
  /** Recipients to offer first (e.g. the support conversation's contact). */
  prefer?: { phone?: string | null; email?: string | null };
  codeId?: string;
  onSent?: (m: PlatformMessage) => void;
  onClose: () => void;
}) {
  const notify = useNotify();
  const send = useSendTenantMessage(tenant.id);
  const detail = useQuery({
    queryKey: tenantKeys.detail(tenant.id),
    queryFn: () => fetchTenant(tenant.id),
  });
  const templates = useTemplates()(template);
  const [channel, setChannel] = useState<Channel>(initial);
  const [to, setTo] = useState<string | null>(null);
  // Edits in progress; null shows the template filled for this shop.
  const [subject, setSubject] = useState<string | null>(null);
  const [body, setBody] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const filled: TemplateVars = {
    name: detail.data?.owners[0]?.name,
    shop: tenant.name,
    shopId: detail.data?.slug,
  };
  const subjectValue = subject ?? fillTemplate(templates?.subject ?? '', filled);
  const bodyValue =
    body ??
    (channel === 'EMAIL'
      ? fillEmailTemplate(templates?.body ?? '', filled) + (extra?.html ?? '')
      : [fillTemplate(templates?.whatsapp ?? '', filled), extra?.text]
          .filter(Boolean)
          .join('\n\n'));

  // The shop's owners first, then the shop's own phone / email; no duplicates.
  const options = useMemo(() => {
    const d = detail.data;
    if (!d) return [];
    const all =
      channel === 'WHATSAPP'
        ? [prefer?.phone, ...d.owners.map((o) => o.phone), d.contact?.phone]
        : [prefer?.email, ...d.owners.map((o) => o.email), d.contact?.email];
    return [...new Set(all.filter((v): v is string => !!v?.trim()))];
  }, [detail.data, channel, prefer?.phone, prefer?.email]);
  const toValue = to ?? options[0] ?? '';
  const email = channel === 'EMAIL';
  const valid =
    toValue.trim().length >= 3 &&
    (email ? htmlText(bodyValue) : bodyValue.trim()).length > 0 &&
    (!email || subjectValue.trim().length > 0);

  const submit = () => {
    setError(null);
    send.mutate(
      {
        channel,
        to: toValue.trim(),
        body: bodyValue.trim(),
        ...(email ? { subject: subjectValue.trim() } : {}),
        ...(codeId ? { codeId } : {}),
      },
      {
        onSuccess: (m) => {
          notify.success(email ? 'Email sent' : 'WhatsApp message sent');
          onSent?.(m);
          onClose();
        },
        onError: setError,
      },
    );
  };

  return (
    <Dialog open onClose={send.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Message {tenant.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          {error !== null && <Callout severity="error">{errorMessage(error)}</Callout>}
          <ToggleButtonGroup
            exclusive
            size="small"
            value={channel}
            onChange={(_, v: Channel | null) => {
              if (!v) return;
              setChannel(v);
              setTo(null);
              setBody(null);
            }}
            aria-label="Send by"
          >
            <ToggleButton value="WHATSAPP" aria-label="WhatsApp">
              <WhatsAppIcon fontSize="small" sx={{ mr: 0.75, color: WHATSAPP_GREEN }} /> WhatsApp
            </ToggleButton>
            <ToggleButton value="EMAIL" aria-label="Email">
              <EmailRounded fontSize="small" sx={{ mr: 0.75 }} /> Email
            </ToggleButton>
          </ToggleButtonGroup>
          <TextField
            label={email ? 'Email address' : 'WhatsApp number'}
            value={toValue}
            onChange={(e) => setTo(e.target.value)}
            placeholder={email ? 'owner@shop.com' : '0300 1234567'}
            slotProps={{ htmlInput: { inputMode: email ? 'email' : 'tel' } }}
            helperText={
              email ? undefined : 'Local numbers get +92; write others with + and the country code.'
            }
            required
          />
          {options.length > 1 && (
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
              {options.map((o) => (
                <Chip
                  key={o}
                  size="small"
                  label={o}
                  variant={o === toValue ? 'filled' : 'outlined'}
                  onClick={() => setTo(o)}
                />
              ))}
            </Stack>
          )}
          {email && (
            <TextField
              label="Subject"
              value={subjectValue}
              onChange={(e) => setSubject(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 300 } }}
              required
            />
          )}
          {email ? (
            // Keyed by template: a loaded template replaces the content, edits stay.
            <RichTextEditor
              key={templates ? 'ready' : 'loading'}
              label="Message"
              value={bodyValue}
              onChange={setBody}
            />
          ) : (
            <TextField
              label="Message"
              value={bodyValue}
              onChange={(e) => setBody(e.target.value)}
              multiline
              minRows={4}
              maxRows={12}
              slotProps={{ htmlInput: { maxLength: 4000 } }}
              required
            />
          )}
          <Typography variant="caption" color="text.secondary">
            Sent from PackBytes’ own {email ? 'mail server' : 'WhatsApp number'} (Settings) and kept
            on the shop’s Messages tab.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={send.isPending}>
          Cancel
        </Button>
        <Button variant="contained" loading={send.isPending} disabled={!valid} onClick={submit}>
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
}
